import { EPaymentChannel } from '../../../types/common';
import { getAccOpeningSaveParam } from '../../../api/lianlian-utils';
import { getYibaoPayeeSaveParam } from '../../../api/yibao/yibao-utils';
import { IAccOpening, IBeneficiarySaveParam, ILegalPersonSaveParam, ILicenseSaveParam, IPersonalInfo } from '../../../src/components';
import { EPayeeUserStatus, ICompanyPayeeExcelImportResult, IPayeeApplySubmit, IPayeeItem, IPayeeParam } from '../../../types/air-splitting/separate-bills/payeeManagement';
import { ELianlianCardStatus, ILianDetail } from '../../../types/lianlian';
import { IYibaoPayee } from '../../../types/yibao';
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getFileExt, getHostbaseUrl } from '@core/tools';
import { IStepItem } from '@vue3/components';

const hostUrl = getHostbaseUrl();

/**
 * 空中分账 - 收款方 - 启用-禁用
 */
export async function changePayeeStatus(payeeId: string, status: EPayeeUserStatus) {
  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/pay/payee/change-status`, {
      payeeId,
      status,
    });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 分账管理 - 收款方管理
 */
export async function queryPayees(param: IPayeeParam) {
  let data = [] as IPayeeItem[];
  let total = 0;

  param = JSON.parse(JSON.stringify(param)) as IPayeeParam;
  param.payeeType = param.payeeType || undefined;

  try {
    const { code, context, message } = await Http.get<{ data: IPayeeItem[]; total: number }>(`${hostUrl}/pay/payee/page`, param);

    if (code === EAxiosResponseCode.Succeed) {
      const { data: items, total: all } = context || {} as { data: IPayeeItem[]; total: number };

      data = resolvePayeeItems(items || []);
      total = all || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 空中分账 - 收款方 - 查询账户详细信息
 */
export async function queryPayeeDetail(lianId: number) {
  let data = {} as ILianDetail;

  try {
    const { code, context, message } = await Http.get<any>(`${hostUrl}/pay/payee/detail`, { lianId });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || {} as ILianDetail;
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 收款方 - 查询易宝账户详细信息
 */
export async function queryYiobaoPayeeDetail(id: number) {
  let data = {} as IYibaoPayee;

  try {
    const { code, context, message } = await Http.get<IYibaoPayee>(`${hostUrl}/pay/yop/company/payee/detail`, { id });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || {} as IYibaoPayee;
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 收款方 - 查询易宝商户简单信息
 */
export async function queryYopMerchantSimpleInfo(merchantNo?: string) {
  let data = [] as {merchantNo: string;merchantName: string}[];

  try {
    const { code, context, message } = await Http.get<{merchantNo: string;merchantName: string}[]>(`${hostUrl}/pay/yop/company/list-query/merchant-simple-info`, { merchantNo });

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      data = context || [];
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 空中分账 - 收款方 - 个人基本信息保存
 */
export async function savePersonalBasic(param: IPersonalInfo) {
  let lianId = '';

  param = JSON.parse(JSON.stringify(param)) as IPersonalInfo;
  // @ts-ignore
  param.lianNo = undefined;

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/pay/payee/save-personal-basic-info`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      lianId = context || '';
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return Number(lianId);
}

/**
 * 空中分账 - 收款方 - 个人绑卡信息保存
 */
export async function savePersonalBankInfo(param: Partial<IAccOpening>) {
  param = JSON.parse(JSON.stringify(param)) as IAccOpening;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payee/save-personal-bank-info`,
      {
        bankNo: param.bankNo,
        phoneNo: param.phoneNo,
        lianId: param.lianId,
        lianNo: param.lianNo,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 上传营业执照信息保存
 */
export async function saveLicense(
  param: ILicenseSaveParam,
) {
  let lianId = '';

  param = JSON.parse(JSON.stringify(param)) as ILicenseSaveParam;

  // @ts-ignore
  param.businessLicenseIsLongTerm = param.businessLicenseEndDate === '29991231';
  param.businessLicensePicType = undefined;
  // @ts-ignore
  param.companyName = param.name;
  param.name = undefined;
  param.lianNo = undefined;

  try {
    const { code, context, message } = await Http.post<string>(
      `${hostUrl}/pay/payee/save-business-license`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      lianId = context || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return Number(lianId);
}

/**
 * 空中分账 - 收款方 - 法人信息保存
 */
export async function saveLegalPerson(param: ILegalPersonSaveParam) {
  param = JSON.parse(JSON.stringify(param)) as ILegalPersonSaveParam;

  param.lianNo = undefined;
  param.idNoBackType = undefined;
  param.idNoFrontType = undefined;
  // @ts-ignore
  param.legalPersonIdIsLongTerm = param.idNoExpiryEndDate === '29991231';

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payee/save-legal-person`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 受益人信息保存
 */
export async function saveBeneficiary(param: IBeneficiarySaveParam) {
  param = JSON.parse(JSON.stringify(param)) as IBeneficiarySaveParam;

  param.uboList.forEach((v) => {
    if (v.idNoFrontUrl) {
      v.idNoFrontType = getFileExt(v.idNoFrontUrl);
      v.idNoBackType = getFileExt(v.idNoBackUrl);
    }
  });

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payee/save-ubo`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 开户绑卡信息保存
 */
export async function saveOpenparam(param: IAccOpening) {
  param = JSON.parse(JSON.stringify(param)) as IAccOpening;
  const p = getAccOpeningSaveParam(param as IAccOpening);

  p.bankAccountOpenLicenseType = undefined;

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/payee/save-bank-info`,
      p,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 易宝收款方信息保存
 */
export async function batchImportFromExcel(param: {fileUrl: string}) {
  try {
    const { code, message, context } = await Http.post<ICompanyPayeeExcelImportResult>(
      `${hostUrl}/pay/yop/company/payee/import-from-excel`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      return context || {} as ICompanyPayeeExcelImportResult;
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 易宝收款方信息保存
 */
export async function saveYibaoPayeeParam(param: IYibaoPayee) {
  param = JSON.parse(JSON.stringify(param)) as IYibaoPayee;
  const p = getYibaoPayeeSaveParam(param as IYibaoPayee);

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/yop/company/payee/company-payee`,
      p,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 申请账户 - 提交
 */
export async function submitPayeeAccountInfo(param: IPayeeApplySubmit) {
  let token = '';

  param = JSON.parse(JSON.stringify(param)) as IPayeeApplySubmit;

  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/payee/lian/open-acct`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      token = (context || {}).token || '';
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return token;
}

/**
 * 空中分账 - 收款方 - 申请账户 - 校验个人收款方是否存在
 */
export async function checkPayeePerson(param: { idNumber: string; userName: string }) {
  try {
    const { code, message } = await Http.get<any>(
      `${hostUrl}/pay/payee/lian/check`,
      {
        ...param,
        userType: '1',
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message);
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 申请账户 - 校验企业收款方是否存在
 */
export async function checkPayeeCompany(param: { creditCode: string; companyName: string }) {
  try {
    const { code, message } = await Http.get<any>(
      `${hostUrl}/pay/payee/lian/check`,
      {
        ...param,
        userType: '2',
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message);
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 提交时候发送验证短信
 */
export async function sendPayeeSmscode(regPhone: string, lianId: number) {
  try {
    const { code, message } = await Http.post<string>(
      `${hostUrl}/pay/payee/lian/phone-verify-code-apply`,
      {
        lianId,
        regPhone,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 空中分账 - 收款方 - 最终提交
 */
export async function submitPayeeValid(param: IPayeeValidSubmit) {
  param = JSON.parse(JSON.stringify(param)) as IPayeeValidSubmit;

  try {
    const { code, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/payee/lian/open-acct-verify`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

export interface IPayeeValidSubmit {
  /** 连连Id */
  lianId?: number;
  lianNo?: string;
  token?: string;
  /** 验证码 */
  verifyCode?: string;
  /** 支付密码 */
  payPassword?: string;
  /** 随机因子 */
  randomKey?: string;
}

export enum EPEAccountStoreType {
  /** 个体工商户 */
  Personal = '0',
  /** 企业 */
  Company = '1',
}

export enum EPayeeStepKey {
  License = 'license',
  Legal = 'legal',
  AccOpening = 'accOpening',
  Beneficiary = 'beneficiary',
  Store = 'store',
  Submit = 'submit',
  Submited = 'submited',
  NoPassAuth = 'noPassAuth',
}

export function getPayeeSteps(type: 'company' | 'personal'): IStepItem<EPayeeStepKey>[] {
  const isCompany = type === 'company';

  return [
    {
      title: `上传${isCompany ? '营业执照' : '开户基本信息'}`,
      key: EPayeeStepKey.License,
    },
    isCompany
      ? {
        title: '上传法人信息',
        key: EPayeeStepKey.Legal,
      }
      : undefined,
    {
      title: '上传开户绑卡信息',
      key: EPayeeStepKey.AccOpening,
    },
    isCompany
      ? {
        title: '添加受益人',
        key: EPayeeStepKey.Beneficiary,
      }
      : undefined,
    {
      title: '支付认证',
      key: EPayeeStepKey.Submit,
    },
  ].filter(Boolean);
}

export function getPrevPayeeStep(current: EPayeeStepKey, type: 'company' | 'personal'): IStepItem<EPayeeStepKey> | undefined {
  const steps = getPayeeSteps(type);
  const index = steps.findIndex((item) => item.key === current);

  return steps[index - 1];
}

export function getPayeeStep(current: EPayeeStepKey, type: 'company' | 'personal'): IStepItem<EPayeeStepKey> | undefined {
  const steps = getPayeeSteps(type);

  return steps.find((item) => item.key === current);
}

function resolvePayeeItems(items: IPayeeItem[]) {
  const result: IPayeeItem[] = items;

  for (const v of items) {
    v.showVerify = v.accountStatus === ELianlianCardStatus.ACTIVATE_PENDING_NEW;
    v.showEditYibao = v.channelType === EPaymentChannel.Yibao;
    v.showManage =
      [EPayeeUserStatus.Enable, EPayeeUserStatus.Disable, EPayeeUserStatus.Cancel].includes(v.status)
      && v.channelType === EPaymentChannel.Lianlian;
  }

  return result;
}

